import { Buffer } from 'buffer'
import { useCallback, useEffect, useRef, useState } from "react"
import { getToken, getTokenExpirationMs, logout } from './auth'
import { cleanup } from './role'
import ConfirmDialog from '../form/ConfirmDialog'
import EventBus from '../utils/EventBus'

const logLocal = (...args) => {
  if (window.location.hostname.includes('localhost')) {
    console.log(...args)
  }
}

let _timeout = undefined
let _interval = undefined

export default function SessionTimeout(props={token: undefined}) {
  const [isExtendSessionDialogVisible, setIsExtendSessionDialogVisible] = useState(false)
  // ref so the setInterval closure always reads the current value without being re-created
  const isDialogVisibleRef = useRef(false)
  const lastTokenRef = useRef()

  const parseJwt = (token) => {
    if(token) {
      return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
    } else {
      console.error("Trying to parse a jwt token, but no token was provided.")
      return false
    }
  }

  const clearMonitors = useCallback(() => {
    if (_timeout !== undefined) {
      clearTimeout(_timeout)
      _timeout = undefined 
    }
    if (_interval !== undefined) {
      clearInterval(_interval)
      _interval = undefined
    }
  }, [])

  const login = useCallback(async () => {
    return await getToken()
  }, [])

  const extendSession = useCallback(async () => {
    clearMonitors()
    setIsExtendSessionDialogVisible(false)
    isDialogVisibleRef.current = false
    await login()
  }, [clearMonitors, login])

  const _logout = useCallback(async () => {
    clearMonitors()
    setIsExtendSessionDialogVisible(false)
    cleanup()
    logout()
  }, [clearMonitors])

  const createSessionMonitor = useCallback(() => {
    if (_interval !== undefined) clearInterval(_interval)
      
    _interval = setInterval(async () => {
      const jwt = await getToken(true)
      const exp = getTokenExpirationMs(jwt)
      if (exp > 0) {
        let warningGracePeriod = 5 * 60 * 1000
        const now = new Date().getTime()
        logLocal(`Session Interval: warning in ${parseInt((exp - warningGracePeriod - now) / 60 / 1000)} minutes`)

        if (now > exp) {
          extendSession()
        } else if (now + warningGracePeriod > exp) {
          if (!isDialogVisibleRef.current) {
            isDialogVisibleRef.current = true
            setIsExtendSessionDialogVisible(true)
            // fire at actual expiry, not a fixed 5 min that may overshoot it
            _timeout = setTimeout(extendSession, exp - now)
          }
        }
      } else {
        logLocal('Could not extract exp')
        _logout()
      }
    }, 30 * 1000)
  }, [_logout, extendSession])

  const initialize = useCallback((_token) => {
    lastTokenRef.current = _token
    const jwt = parseJwt(_token?.idToken)
    clearMonitors()
    
    if(jwt) {
      let ttl = (jwt.exp - jwt.iat) / 60
      logLocal(`Server Session duration: ${ttl} minutes. Expire at ${new Date(jwt.exp * 1000).toLocaleString()}`)

      createSessionMonitor()

      } else {
        logLocal(`No Token`)
        _logout()
      }
  }, [clearMonitors, createSessionMonitor, _logout])

  const handleToken = useCallback((token) => {
    logLocal(`handleToken`, token)
    initialize(token)
  }, [initialize])

  useEffect(() => {
    isDialogVisibleRef.current = isExtendSessionDialogVisible
  }, [isExtendSessionDialogVisible])

  useEffect(() => {
    EventBus.on('token', handleToken)
    if (props.token && props.token !== lastTokenRef.current) {
      initialize(props.token)
    } else if (!props.token) {
      getToken().then((token) => {
        if (token) {
          initialize(token)
        }
      })
    }
    return () => {
      EventBus.removeListener('token', handleToken)
      clearMonitors()
    }
  }, [clearMonitors, handleToken, initialize, props.token])

  return (
    <>
    {isExtendSessionDialogVisible && <ConfirmDialog title="Session token expiring soon" text="We haven't fetched a token for you in a while and the current one is expiring, just checking if you are still here." open={isExtendSessionDialogVisible} handleConfirm={_logout} hideDialog={extendSession}  noLabel="Still Working" noButtonVariant={'contained'} yesLabel="Log Out" yesButtonVariant={'outlined'} />}
    </>
  )
}